import React, { useState, useRef, lazy } from 'react';
import { ChevronLeft, ChevronRight, LayersIcon, ClipboardListIcon, PresentationIcon, PaletteIcon, ImagesIcon, ArrowRightIcon, FolderOpenIcon } from 'lucide-react';
const items = [{
  icon: LayersIcon,
  title: 'I 5 Tomi del Percorso Graduato',
  desc: 'Una guida completa per ogni classe (dalla 1ª alla 5ª) con programmazione mensile, obiettivi e laboratori ad alto impatto visuale.',
  chip: 'PDF · 5 tomi',
  accent: 'blue'
}, {
  icon: ClipboardListIcon,
  title: "Il Registro dell'Esperto",
  desc: 'La soluzione definitiva alla burocrazia: griglie di valutazione descrittiva (secondo le nuove norme ministeriali), verbali e registri pronti da compilare.',
  chip: 'PDF compilabile',
  accent: 'green'
}, {
  icon: PresentationIcon,
  title: "Slide per la LIM: Storia dell'Arte Visuale",
  desc: "Lezioni digitali per raccontare i grandi maestri (Leonardo, Van Gogh, Picasso) catturando l'attenzione di ogni bambino.",
  chip: 'Slide LIM',
  accent: 'purple'
}, {
  icon: PaletteIcon,
  title: 'Manuale Pratico del Disegno e del Colore',
  desc: 'Schede operative per insegnare le tecniche base (punti, linee, chiaroscuro) in modo semplice e divertente.',
  chip: 'Schede stampabili',
  accent: 'orange'
}];
const accentMap: Record<string, {
  iconBg: string;
  iconBorder: string;
  iconText: string;
  chipBg: string;
  chipText: string;
  glow: string;
  bar: string;
  barShadow: string;
  hoverBorder: string;
}> = {
  blue: {
    iconBg: 'from-sky-500/20 to-sky-600/5',
    iconBorder: 'border-sky-500/30',
    iconText: 'text-sky-400',
    chipBg: 'bg-sky-500/10 border-sky-500/30',
    chipText: 'text-sky-400',
    glow: 'rgba(56,189,248,0.18)',
    bar: 'bg-sky-500',
    barShadow: 'shadow-[0_0_15px_rgba(56,189,248,0.5)]',
    hoverBorder: 'hover:border-sky-500/50'
  },
  green: {
    iconBg: 'from-emerald-500/20 to-emerald-600/5',
    iconBorder: 'border-emerald-500/30',
    iconText: 'text-emerald-400',
    chipBg: 'bg-emerald-500/10 border-emerald-500/30',
    chipText: 'text-emerald-400',
    glow: 'rgba(16,185,129,0.18)',
    bar: 'bg-emerald-500',
    barShadow: 'shadow-[0_0_15px_rgba(16,185,129,0.5)]',
    hoverBorder: 'hover:border-emerald-500/50'
  },
  purple: {
    iconBg: 'from-purple-500/20 to-purple-600/5',
    iconBorder: 'border-purple-500/30',
    iconText: 'text-purple-400',
    chipBg: 'bg-purple-500/10 border-purple-500/30',
    chipText: 'text-purple-400',
    glow: 'rgba(168,85,247,0.18)',
    bar: 'bg-purple-500',
    barShadow: 'shadow-[0_0_15px_rgba(168,85,247,0.5)]',
    hoverBorder: 'hover:border-purple-500/50'
  },
  orange: {
    iconBg: 'from-orange-500/20 to-orange-600/5',
    iconBorder: 'border-orange-500/30',
    iconText: 'text-orange-400',
    chipBg: 'bg-orange-500/10 border-orange-500/30',
    chipText: 'text-orange-400',
    glow: 'rgba(249,115,22,0.18)',
    bar: 'bg-orange-500',
    barShadow: 'shadow-[0_0_15px_rgba(249,115,22,0.5)]',
    hoverBorder: 'hover:border-orange-500/50'
  }
};
const carouselImages = [{
  src: 'https://cdn.magicpatterns.com/uploads/2o2w4mTPjf77CFGTqx7GmT/Captura-de-pantalla-2026-04-22-a-las-4.04.50_p.-m..webp',
  alt: 'Il Cerchio Magico dei Colori'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/dUHBRynWgHuJ9wGToXnniu/Captura-de-pantalla-2026-04-22-a-las-4.05.26_p.-m..webp',
  alt: "Colori Freddi: Il Ghiaccio dell'Inverno"
}, {
  src: 'https://cdn.magicpatterns.com/uploads/a6vzL8YfbY7nEBxAeGjBo8/Captura-de-pantalla-2026-04-22-a-las-4.06.00_p.-m..webp',
  alt: "Inclusione: L'Arte Accessibile a Tutti"
}, {
  src: 'https://cdn.magicpatterns.com/uploads/8napjJbFXNA3V4Zr6o1kMF/Captura-de-pantalla-2026-04-22-a-las-4.06.33_p.-m..webp',
  alt: 'La Teoria dei Contrasti'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/brBePWe9gZ9j84ez2vLK2L/Captura-de-pantalla-2026-04-22-a-las-4.06.53_p.-m..webp',
  alt: "L'Inquadratura"
}, {
  src: 'https://cdn.magicpatterns.com/uploads/xnR93ymue6gi6zM3SKfYfW/Captura-de-pantalla-2026-04-22-a-las-4.06.53_p.-m..webp',
  alt: "L'Inquadratura — Dettaglio"
}];
export function ContentPreview() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const rafRef = useRef<number>(0);
  const handleScroll = () => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      if (!scrollRef.current) return;
      const container = scrollRef.current;
      const scrollLeft = container.scrollLeft;
      const clientWidth = container.clientWidth;
      const center = scrollLeft + clientWidth / 2;
      const children = container.children;
      let minDistance = Infinity;
      let active = 0;
      for (let i = 0; i < children.length; i++) {
        const child = children[i] as HTMLElement;
        const childCenter = child.offsetLeft + child.offsetWidth / 2;
        const distance = Math.abs(center - childCenter);
        if (distance < minDistance) {
          minDistance = distance;
          active = i;
        }
      }
      setActiveIndex(active);
    });
  };
  const scrollToIndex = (index: number) => {
    if (!scrollRef.current) return;
    const container = scrollRef.current;
    const child = container.children[index] as HTMLElement;
    if (!child) return;
    const scrollLeft = child.offsetLeft - container.clientWidth / 2 + child.clientWidth / 2;
    container.scrollTo({
      left: scrollLeft,
      behavior: 'smooth'
    });
  };
  const scrollPrev = () => {
    if (activeIndex > 0) scrollToIndex(activeIndex - 1);
  };
  const scrollNext = () => {
    if (activeIndex < carouselImages.length - 1) scrollToIndex(activeIndex + 1);
  };
  return <section className="relative w-full bg-bg-dark py-16 sm:py-24 px-4 overflow-hidden" data-id="element-59">
      {/* Background glows */}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full pointer-events-none" style={{
      background: 'radial-gradient(circle, rgba(56,189,248,0.06) 0%, transparent 70%)'
    }} aria-hidden="true" data-id="element-60"></div>
      <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full pointer-events-none" style={{
      background: 'radial-gradient(circle, rgba(168,85,247,0.06) 0%, transparent 70%)'
    }} aria-hidden="true" data-id="element-61"></div>

      <div className="relative max-w-6xl mx-auto z-10" data-id="element-62">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-14" data-id="element-63">
          <span className="inline-flex items-center gap-1.5 text-primary font-bold text-[11px] sm:text-xs uppercase tracking-[0.15em] bg-primary/10 border border-primary/20 px-3 py-1.5 rounded-full mb-4" data-id="element-64">
            <FolderOpenIcon className="w-3.5 h-3.5" data-id="element-65" />
            Cosa c'è dentro
          </span>
          <h2 className="text-3xl sm:text-5xl font-black text-text-main tracking-tight leading-[1.1] mb-4" data-id="element-66">
            L'Archivio{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-300 via-primary to-orange-500" data-id="element-67">
              "Arte Pronta"
            </span>{' '}
            in dettaglio.
          </h2>
          <p className="text-text-sec text-base sm:text-lg max-w-2xl mx-auto leading-relaxed" data-id="element-68">
            Strumenti digitali in PDF{' '}
            <span className="text-text-main font-bold" data-id="element-69">
              pronti da stampare o proiettare
            </span>{' '}
            da oggi stesso.
          </p>
        </div>

        {/* Content cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-20 sm:mb-28" data-id="element-70">
          {items.map((item, i) => {
          const Icon = item.icon;
          const accent = accentMap[item.accent];
          return <div key={i} className={`group relative bg-bg-alt rounded-2xl sm:rounded-3xl border border-border-light ${accent.hoverBorder} overflow-hidden transition-all duration-300 hover:-translate-y-1`} data-id="element-71">
                {/* Top accent bar */}
                <div className={`${accent.bar} h-1 w-full ${accent.barShadow}`} data-id="element-72"></div>

                {/* Hover glow */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{
              background: `radial-gradient(circle at top right, ${accent.glow} 0%, transparent 60%)`
            }} aria-hidden="true" data-id="element-73"></div>

                {/* Number */}
                <span className="absolute top-5 right-5 text-text-sec/40 font-black text-xs tracking-wider z-10" data-id="element-74">
                  0{i + 1}
                </span>

                <div className="relative z-10 p-6 sm:p-8" data-id="element-75">
                  {/* Icon */}
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br ${accent.iconBg} border ${accent.iconBorder} flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-[-4deg]`} data-id="element-76">
                    <Icon className={`w-6 h-6 sm:w-7 sm:h-7 ${accent.iconText}`} strokeWidth={2} data-id="element-77" />
                  </div>

                  {/* Chip */}
                  <span className={`inline-flex items-center text-[10px] sm:text-xs font-black uppercase tracking-wider ${accent.chipBg} ${accent.chipText} border px-2.5 py-1 rounded-full mb-3`} data-id="element-78">
                    {item.chip}
                  </span>

                  {/* Title */}
                  <h3 className="text-lg sm:text-xl font-black text-text-main leading-snug mb-2" data-id="element-79">
                    {item.title}
                  </h3>

                  {/* Description */}
                  <p className="text-text-sec text-sm sm:text-base leading-relaxed" data-id="element-80">
                    {item.desc}
                  </p>
                </div>
              </div>;
        })}
        </div>

        {/* Carousel Section */}
        <div className="relative" data-id="element-81">
          {/* Carousel header */}
          <div className="text-center mb-8 sm:mb-10" data-id="element-82">
            <span className="inline-flex items-center gap-1.5 text-secondary font-bold text-[11px] sm:text-xs uppercase tracking-[0.15em] bg-secondary/10 border border-secondary/30 px-3 py-1.5 rounded-full mb-4" data-id="element-83">
              <ImagesIcon className="w-3.5 h-3.5" data-id="element-84" />
              Anteprima reale
            </span>
            <h3 className="text-2xl sm:text-3xl font-black text-text-main tracking-tight mb-3" data-id="element-85">
              Sfoglia il materiale all'interno
            </h3>
            <div className="flex items-center justify-center gap-3 text-text-sec text-sm" data-id="element-86">
              <span className="inline-flex items-center gap-1.5 sm:hidden text-text-sec/70" data-id="element-87">
                <ChevronLeft className="w-4 h-4 animate-pulse-composited" data-id="element-88" />
                Scorri
                <ChevronRight className="w-4 h-4 animate-pulse-composited" data-id="element-89" />
              </span>
              <span className="inline-flex items-center gap-1 bg-bg-alt border border-border-light px-3 py-1 rounded-full text-xs font-bold" data-id="element-90">
                <span className="text-primary" data-id="element-91">{activeIndex + 1}</span>
                <span className="text-text-sec" data-id="element-92">/ {carouselImages.length}</span>
              </span>
            </div>
          </div>

          <div className="relative group max-w-[100vw] -mx-4 sm:mx-0" data-id="element-93">
            {/* Navigation buttons */}
            <button onClick={scrollPrev} className={`hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-bg-alt hover:bg-bg-main border border-border-light hover:border-primary/50 p-3 rounded-full text-text-main transition-all hover:scale-110 shadow-lg ${activeIndex === 0 ? 'opacity-0 pointer-events-none' : 'opacity-100'}`} aria-label="Precedente" data-id="element-94">
              <ChevronLeft className="w-5 h-5" data-id="element-95" />
            </button>
            <button onClick={scrollNext} className={`hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-bg-alt hover:bg-bg-main border border-border-light hover:border-primary/50 p-3 rounded-full text-text-main transition-all hover:scale-110 shadow-lg ${activeIndex === carouselImages.length - 1 ? 'opacity-0 pointer-events-none' : 'opacity-100'}`} aria-label="Successivo" data-id="element-96">
              <ChevronRight className="w-5 h-5" data-id="element-97" />
            </button>

            {/* Carousel */}
            <div ref={scrollRef} onScroll={handleScroll} className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide gap-4 sm:gap-6 px-[10vw] sm:px-[calc(50%-175px)] pb-8 pt-4" data-id="element-98">
              {carouselImages.map((img, i) => <div key={i} className={`relative snap-center shrink-0 w-[80vw] sm:w-[350px] bg-bg-alt border rounded-2xl overflow-hidden p-2 sm:p-3 flex items-center justify-center transition-all duration-500 ${activeIndex === i ? 'scale-100 opacity-100 border-primary/40 shadow-[0_20px_50px_rgba(249,115,22,0.25)]' : 'scale-95 opacity-50 border-border-light shadow-[0_10px_30px_rgba(0,0,0,0.3)]'}`} data-id="element-99">
                  <img src={img.src} alt={img.alt} loading="lazy" decoding="async" width={800} height={1131} className="w-full h-auto max-h-[400px] sm:max-h-[450px] object-contain rounded-xl bg-white/5" data-id="element-100" />
                  {/* Caption only on active */}
                  {activeIndex === i && <div className="absolute bottom-4 left-4 right-4 bg-bg-dark/90 backdrop-blur-sm border border-border-light rounded-lg px-3 py-2 text-center animate-[fadeInUp_0.3s_ease-out]" data-id="element-101">
                      <p className="text-text-main text-xs sm:text-sm font-bold truncate" data-id="element-102">
                        {img.alt}
                      </p>
                    </div>}
                </div>)}
            </div>
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-0 mt-1" data-id="element-103">
            {carouselImages.map((_, i) => <button key={i} onClick={() => scrollToIndex(i)} className="flex items-center justify-center min-w-[44px] min-h-[44px] p-3" aria-label={`Vai all'immagine ${i + 1}`} data-id="element-104">
                <span className={`block h-2 rounded-full transition-all duration-300 ${i === activeIndex ? 'bg-primary w-8 shadow-[0_0_10px_rgba(249,115,22,0.5)]' : 'bg-border-light hover:bg-text-sec w-2'}`} data-id="element-105" />
              </button>)}
          </div>
        </div>

        {/* Closing CTA */}
        <div className="text-center mt-10 sm:mt-14" data-id="element-106">
          <a href="#pricing" className="group inline-flex items-center gap-2 text-text-main hover:text-cta-green font-bold text-base sm:text-lg transition-colors" data-id="element-107">
            <span data-id="element-108">Voglio l'archivio completo</span>
            <ArrowRightIcon className="w-5 h-5 transition-transform group-hover:translate-x-1" strokeWidth={3} data-id="element-109" />
          </a>
        </div>
      </div>
    </section>;
}